import { getCursorColor } from '../utils/cursor';
import trackEvent from '../utils/analytics';
import CursorManagerBase from './CursorManagerBase';
import { type RecordId } from '../shared-types';
import {
  type CursorMessage,
  type CursorPosition,
  type CursorUser,
  type FayeClient,
  type ResolveUserInfo,
} from '../types';

const insecureRandomId = () => Math.floor(Math.random() * 10000000);

// After a cursor hasn't moved for this many milliseconds, remove it.
const CURSOR_INACTIVE_INTERVAL = 30 * 1000;

// Time to show a cursor's name for after it is hovered.
const CURSOR_SHOW_NAME_INTERVAL = 1 * 1000;

// With up to this many cursors we show the name all the time.
const CURSOR_SHOW_NAMES_COUNT = 4;

// Maximum length for displayName before it is truncated.
const DISPLAY_NAME_MAX_LENGTH = 25;

// Track collaboration cursors. Messages are sent over Faye between all
// clients viewing the same document. Each message has the structure over the
// wire:
//
//  {
//    cursorId - Unique ID of this cursor (to handle the same user acting in multiple tabs)
//    userId - Unique ID of this user
//    x - X position of the cursor in canvas coordinates.
//    y - Y position of the cursor in canvas coordinates.
//  }
//
// To avoid sending personal data over the wire, each client resolves the
// userId to a displayable name. We maintain a cache of these names so we
// don't have to lookup for each cursor update.
class CursorManager extends CursorManagerBase {
  // State variables for throttling cursor updates.
  timer?: NodeJS.Timeout;
  sendWait: boolean = false;
  finalSend?: () => void;

  documentPath: string;
  fayeClient: FayeClient;
  subscription: ReturnType<FayeClient['subscribe']>;
  inactiveTimer?: ReturnType<typeof setInterval>;

  localUserInfo: CursorPosition;
  cursors: Record<RecordId, CursorPosition>;
  users: Record<RecordId, CursorUser> = {};

  resolveUserInfo: ResolveUserInfo;

  _hasTrackedCollaboration = false;

  constructor({ fayeClient, documentPath, userId, resolveUserInfo, cursors }) {
    super();
    // Object listing current cursors, keyed by cursorId.
    this.cursors = cursors;
    this.documentPath = documentPath;
    this.fayeClient = fayeClient;
    this.resolveUserInfo = resolveUserInfo;
    this.localUserInfo = {
      userId,
      cursorId: insecureRandomId(),
      userInfo: { displayName: '' },
    };

    // Run a periodic timer to remove inactive cursors.
    this.inactiveTimer = setInterval(() => this.removeInactiveCursors(), 500);

    this.subscription = this.fayeClient.subscribe<CursorMessage>(
      `/selections/${documentPath}`,
      message => this.updateCursor(message)
    );
  }

  destroy() {
    this.stopSynchronizing();
  }

  removeInactiveCursors() {
    // When too many cursors are visible, hide the cursor details.
    const tooManyCursors =
      Object.keys(this.cursors).length > CURSOR_SHOW_NAMES_COUNT;

    Object.values(this.cursors).forEach(cursor => {
      cursor.hideName = tooManyCursors;

      if (
        cursor.receivedAt &&
        cursor.receivedAt < Date.now() - CURSOR_INACTIVE_INTERVAL
      ) {
        delete this.cursors[cursor.cursorId];
      }
      if (
        cursor.showNameAt &&
        cursor.showNameAt < Date.now() - CURSOR_SHOW_NAME_INTERVAL
      ) {
        cursor.forceShowName = false;
      }
    });
  }

  lookupUser(cursor: CursorPosition) {
    if (!this.users[cursor.userId]) {
      cursor.userInfo = this.users[cursor.userId] = {
        displayName: 'Loading...',
        avatar: undefined,
      };

      this.resolveUserInfo(cursor.userId).then(({ displayName, avatar }) => {
        this.users[cursor.userId].isLoaded = true;
        this.users[cursor.userId].avatar = avatar;

        // Long displayNames must be truncated and an ellipsis added in its
        // place
        if (displayName.length > DISPLAY_NAME_MAX_LENGTH) {
          displayName = `${displayName.substr(0, DISPLAY_NAME_MAX_LENGTH)}…`;
        }

        this.users[cursor.userId].displayName = displayName;
      });
    } else {
      cursor.userInfo = this.users[cursor.userId];
    }
  }

  stopSynchronizing() {
    clearTimeout(this.inactiveTimer);
    this.inactiveTimer = undefined;
    this.subscription.cancel();
  }

  // Receive a cursor update from the server.
  updateCursor(message: CursorMessage) {
    // Ignore our own changes.
    if (message.cursorId === this.localUserInfo.cursorId) return;

    if (!this._hasTrackedCollaboration) {
      this._hasTrackedCollaboration = true;
      trackEvent(
        'Feature-7094629539429452113',
        'Users collaborate on a whiteboard'
      );
    }

    let newCursor = this.cursors[message.cursorId];
    if (!newCursor) {
      newCursor = this.cursors[message.cursorId] = {
        cursorId: message.cursorId,
        userId: message.userId,
      };
      newCursor.color = getCursorColor(newCursor);

      this.lookupUser(newCursor);
    }
    newCursor.x = message.x;
    newCursor.y = message.y;
    newCursor.receivedAt = Date.now();
  }

  // Send a cursor update to the server.
  sendCursor(point) {
    const sendData = () => {
      const cursor: CursorMessage = {
        x: point.x,
        y: point.y,
        cursorId: this.localUserInfo.cursorId,
        userId: this.localUserInfo.userId,
      };

      this.fayeClient.publish(`/selections/${this.documentPath}`, cursor);
    };

    // Throttle sending updates, making sure to send the final position
    // when the cursor stops moving.
    if (!this.timer) {
      this.sendWait = true;
      sendData();
      this.finalSend = undefined;
      this.timer = setTimeout(() => {
        this.timer = undefined;
        if (this.finalSend) this.finalSend();
      }, 10);
    } else {
      this.finalSend = sendData;
    }
  }

  showCursorName(cursorId) {
    const cursor = this.cursors[cursorId];
    if (cursor) {
      cursor.showNameAt = Date.now();
      cursor.forceShowName = true;
    }
  }
}

export default CursorManager;
